import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { ThemeToggle } from '../../components/ThemeToggle';
import { Breadcrumbs } from '../../components/navigation/NavigationHelpers';
import { Package, Star, ChevronLeft, ShoppingCart, Store } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function ProductDetailPage() {
  const { productId } = useParams();
  const [product, setProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadProduct();
  }, [productId]);

  const loadProduct = async () => {
    try {
      const res = await fetch(`${API_URL}/api/commerce/products/${productId}`, {
        credentials: 'include'
      });

      if (res.ok) {
        const data = await res.json();
        setProduct(data.product || data);
      }
    } catch (error) {
      console.error('Failed to load product:', error);
    } finally {
      setLoading(false);
    }
  };

  const addToCart = async () => {
    try {
      const res = await fetch(`${API_URL}/api/commerce/cart/add`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          product_id: productId,
          quantity
        })
      });

      if (res.ok) {
        toast.success('Added to cart!');
      } else {
        toast.error('Failed to add to cart');
      }
    } catch (error) {
      toast.error('Failed to add to cart');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p>Loading product...</p>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center">
        <Card>
          <CardContent className="p-12 text-center">
            <Package className="w-16 h-16 mx-auto text-gray-400 mb-4" />
            <p className="text-gray-600 mb-4">Product not found</p>
            <Button onClick={() => navigate('/commerce/marketplace')}>Back to Marketplace</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      {/* Theme Toggle */}
      <div className="fixed top-4 right-4 z-50">
        <ThemeToggle size="icon" className="bg-white dark:bg-gray-800 shadow-lg" />
      </div>

      <div className="max-w-7xl mx-auto p-6">
        {/* Back Button */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(-1)}
          className="mb-4"
        >
          <ChevronLeft className="w-4 h-4 mr-1" />
          Back
        </Button>

        {/* Breadcrumbs */}
        <Breadcrumbs items={[
          { label: 'Commerce', href: '/commerce/marketplace' },
          { label: product.store_name || 'Store', href: product.store_id ? `/commerce/store/${product.store_id}` : null },
          { label: product.title || product.name }
        ]} />

        {/* Product Details */}
        <div className="grid md:grid-cols-2 gap-8">
          {/* Product Images */}
          <div>
            <Card className="overflow-hidden">
              <div className="aspect-square bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                {product.images?.[0] ? (
                  <img src={product.images[0]} alt={product.title || product.name} className="w-full h-full object-cover" />
                ) : (
                  <Package className="w-32 h-32 text-gray-400" />
                )}
              </div>
            </Card>
            {product.images && product.images.length > 1 && (
              <div className="grid grid-cols-4 gap-2 mt-4">
                {product.images.slice(1, 5).map((img, idx) => (
                  <div key={idx} className="aspect-square bg-gray-100 dark:bg-gray-800 rounded-lg overflow-hidden">
                    <img src={img} alt={`${product.title || product.name} ${idx + 2}`} className="w-full h-full object-cover" />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Product Info */}
          <div className="space-y-6">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{product.title || product.name}</h1>
                {product.featured && <Badge variant="default">Featured</Badge>}
              </div>
              <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
                <button 
                  onClick={() => product.store_id && navigate(`/commerce/store/${product.store_id}`)}
                  className="flex items-center gap-1 hover:text-blue-600"
                >
                  <Store className="w-4 h-4" />
                  {product.store_name || 'Store'}
                </button>
                {product.category && <Badge variant="secondary">{product.category}</Badge>}
              </div>
            </div>

            <div>
              <p className="text-4xl font-bold text-blue-600">{product.price} {product.currency}</p>
              {product.stock !== null && product.stock !== undefined && (
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  {product.stock > 0 ? `${product.stock} in stock` : 'Out of stock'}
                </p>
              )}
            </div>

            <div>
              <h3 className="font-semibold mb-2">Description</h3>
              <p className="text-gray-600 dark:text-gray-400">{product.description}</p>
            </div>

            {product.stock === null || product.stock === undefined || product.stock > 0 ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Quantity</label>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    >
                      -
                    </Button>
                    <span className="px-4 py-2 border rounded">{quantity}</span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setQuantity(quantity + 1)}
                    >
                      +
                    </Button>
                  </div>
                </div>

                <div className="flex gap-3">
                  <Button onClick={addToCart} className="flex-1" size="lg">
                    <ShoppingCart className="w-5 h-5 mr-2" />
                    Add to Cart
                  </Button>
                  <Button 
                    variant="outline" 
                    size="lg"
                    onClick={() => {
                      addToCart();
                      setTimeout(() => navigate('/commerce/cart'), 500);
                    }}
                  >
                    Buy Now
                  </Button>
                </div>
              </div>
            ) : (
              <div className="bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg p-4">
                <p className="text-red-600 dark:text-red-400 font-semibold">This product is currently out of stock</p>
              </div>
            )}
          </div>
        </div>

        {/* Additional Information */}
        <div className="mt-8 grid md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-4 text-center">
              <Package className="w-8 h-8 mx-auto mb-2 text-blue-500" />
              <p className="font-semibold">Free Shipping</p>
              <p className="text-sm text-muted-foreground">On orders over 100 MTR</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <Star className="w-8 h-8 mx-auto mb-2 text-yellow-500" />
              <p className="font-semibold">Quality Guaranteed</p>
              <p className="text-sm text-muted-foreground">Verified sellers only</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <ShoppingCart className="w-8 h-8 mx-auto mb-2 text-green-500" />
              <p className="font-semibold">Secure Payment</p>
              <p className="text-sm text-muted-foreground">Protected by Matrise Wallet</p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}